import { flatMap, get } from "lodash";
import { useMemo, useCallback } from "react";
import ApiClient from "../../../lib/apiClient";
import DocumentTemplateApiClient from "./documentTemplateApiClient";

export const useDocumentTemplateSchema = (
  apiClient: ApiClient,
  route?: string
) => {
  // Memoize the DocumentTemplateApiClient to prevent recreation on every render
  const documentTemplateApiClient = useMemo(() => {
    return new DocumentTemplateApiClient(apiClient);
  }, [apiClient]);

  // Memoize the getDocumentTemplateSchema function
  const getDocumentTemplateSchema = useCallback(async () => {
    const documentTemplateSchema =
      await documentTemplateApiClient.getDocumentTemplateSchema(route);
    return documentTemplateSchema;
  }, [documentTemplateApiClient, route]);

  // Memoize the getFlattenedDocumentTemplateSchema function
  const getFlattenedDocumentTemplateSchema = useCallback(
    async (modelName: string) => {
      const documentTemplateSchema = await getDocumentTemplateSchema();

      const sampleModelSchema = get(documentTemplateSchema, modelName);
      const flatSampleModelSchema = flatMap(
        sampleModelSchema,
        (value: any) => {
          return value;
        }
      );
      return flatSampleModelSchema;
    },
    [getDocumentTemplateSchema]
  );

  // Memoize the getDocumentTemplateFieldsData function
  const getDocumentTemplateFieldsData = useCallback(
    async (modelName?: string, modelId?: string) => {
      return documentTemplateApiClient.getDocumentTemplateFieldsData(
        modelName,
        modelId
      );
    },
    [documentTemplateApiClient]
  );

  const updateDocumentTemplateFileFormData = useCallback(
    async (templateId: string, fileFormData: string) => {
      return documentTemplateApiClient.updateDocumentTemplateFileFormData(
        templateId,
        fileFormData
      );
    },
    [documentTemplateApiClient]
  );

  const populateDocument = useCallback(
    async (
      file?: string,
      fileFormData?: string,
      templateFieldsData?: Record<string, string>
    ) => {
      return documentTemplateApiClient.populateDocument(
        file,
        fileFormData,
        templateFieldsData
      );
    },
    [documentTemplateApiClient]
  );

  return {
    getDocumentTemplateSchema,
    getFlattenedDocumentTemplateSchema,
    getDocumentTemplateFieldsData,
    populateDocument,
    updateDocumentTemplateFileFormData,
  };
};
